import { type DeepReadonly, defineComponent, onMounted, type PropType } from "vue";
import { Language } from "@utils/type/type";
import type { SelectOption } from "@/components/form/GaFormFieldInputSelect";
import type { GaVueComponent } from "@/common/vueUtils";
import { PushFrequency, type WarmupEditForm, type WarmupWidgetProps } from "@newgenerated/shared/schema";
import "@/components/warmup/warmup.scss";
import { createStore, type Store } from "@/common/storeUtils";
import { getCountryListViews } from "@utils/vue-migration/api/countryApi";
import logo from "@/components/warmup/media/ga-logo-negative.svg?url";
import defaultImage from "@/components/warmup/media/getinspired.png?url";
import type { WarmupFormMeta } from "@/components/warmup/warmupForm";
import type { SubmissionState } from "@/components/form/GaForm";
import type { TranslationFn } from "@utils/vue-migration/common/gaContext/gaContextTranslations";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { ContentCard, getInitialSuspendTranslationFns, type TranslationFnsAsync } from "@/components/cards/ContentCard";
import { WarmupFormSso } from "@/components/warmup/WarmupSso";
import { WarmupFormTrial } from "@/components/warmup/WarmupTrial";

export type WarmupOptions = {
  languages: SelectOption<Language>[];
  countries: SelectOption<string>[];
  frequencies: SelectOption<PushFrequency>[];
};

export type WarmupState = {
  editForm: WarmupEditForm;
  metaForm: WarmupFormMeta;
  submissionState: SubmissionState;
  options: WarmupOptions;
  translationFns: TranslationFnsAsync;
};

export function getLanguageOptions(t: TranslationFn): SelectOption<Language>[] {
  return Language.getValues().map((isoCode): SelectOption<Language> => ({ value: isoCode, label: t(Language.getTmsKey(isoCode), "") }));
}

export function getPushFrequencyOptions(t: TranslationFn): SelectOption<PushFrequency>[] {
  const tmsKeys: Record<PushFrequency, string> = {
    NONE: "warmup2:pushFrequency.NONE",
    DAILY: "warmup2:pushFrequency.DAILY",
    ONCE_A_WEEK: "warmup2:pushFrequency.ONCE_A_WEEK",
    TWICE_A_MONTH: "warmup2:pushFrequency.TWICE_A_MONTH",
    ONCE_A_MONTH: "warmup2:pushFrequency.ONCE_A_MONTH",
  };
  return PushFrequency.getValues().map(
    (frequency): SelectOption<PushFrequency> => ({
      value: frequency,
      label: t(tmsKeys[frequency], ""),
    }),
  );
}

export async function loadCountryOptions(store: Store<SelectOption<string>[]>): Promise<void> {
  const countryListView = await getCountryListViews();
  const countryOptions = countryListView.map(
    ({ countryCode, countryName }): SelectOption<string> => ({
      value: countryCode,
      label: countryName,
    }),
  );
  store.set(countryOptions);
}

function WarmupForm(props: { store: Store<WarmupState>; metaInfo: DeepReadonly<WarmupWidgetProps> }): GaVueComponent {
  const { kind, store } = props.store.sub("editForm").sub("either").unpackUnion();

  switch (kind) {
    case "SSO": {
      return <WarmupFormSso editForm={store} metaForm={props.store.sub("metaForm")} options={props.store.sub("options")} submissionState={props.store.sub("submissionState")} metaInfo={props.metaInfo} />;
    }
    case "TRIAL":
      return <WarmupFormTrial editForm={store} metaForm={props.store.sub("metaForm")} options={props.store.sub("options")} submissionState={props.store.sub("submissionState")} metaInfo={props.metaInfo} />;
  }
}

export function WarmupComponent(props: { store: Store<WarmupState>; metaInfo: DeepReadonly<WarmupWidgetProps> }): GaVueComponent {
  const { t, currentLanguage } = useGaContext();
  return (
    <div class="warmup">
      <aside class="warmup-sidebar">
        <div class="text-center">
          <img src={logo} alt="getAbstract" width="182" height="30" />
        </div>
        <div class="warmup-sidebar-image">
          {props.metaInfo.relayType.kind === "CONTENT" ? <ContentCard layout="vertical" link={"none"} content={props.metaInfo.relayType.contentMetadata} translationFn={props.store.sub("translationFns").sub(currentLanguage())} /> : null}
          {props.metaInfo.relayType.kind === "DEFAULT" ? <img src={defaultImage} alt="" /> : null}
        </div>
        <span class="warmup-sidebar-slogan" vHtml={t("warmup2:slogan")} />
      </aside>
      <aside class="warmup-sidebar--mobile">
        {props.metaInfo.relayType.kind === "CONTENT" ? <span vHtml={t("warmup2:slogan")} /> : null}
        {props.metaInfo.relayType.kind === "CONTENT" ? <ContentCard layout="horizontal" link={"none"} content={props.metaInfo.relayType.contentMetadata} translationFn={props.store.sub("translationFns").sub(currentLanguage())} /> : null}
      </aside>
      <main class="warmup-main">
        <h2>{t("warmup2:intro.heading", [props.metaInfo.corporationName])}</h2>
        <p>{t("warmup2:intro.subheading")}</p>
        <WarmupForm store={props.store} metaInfo={props.metaInfo} />
      </main>
    </div>
  );
}

export const Warmup = defineComponent({
  props: {
    warmupWidgetProps: {
      type: Object as PropType<WarmupWidgetProps>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useGaContext();
    const store = createStore<WarmupState>({
      editForm: props.warmupWidgetProps.form,
      metaForm: {
        errors: { general: [], fields: { additionalProperties: {} } },
        changedFields: { kind: "FIELDS", fields: [] },
      },
      submissionState: "IDLE",
      options: {
        languages: getLanguageOptions(t),
        countries: [],
        frequencies: getPushFrequencyOptions(t),
      },
      translationFns: getInitialSuspendTranslationFns(),
    });

    onMounted(async () => {
      await loadCountryOptions(store.sub("options").sub("countries"));
    });

    return () => <WarmupComponent store={store} metaInfo={props.warmupWidgetProps} />;
  },
});
